import React from 'react';
import { Loader2, Briefcase, Search, Plus, ArrowRight, XCircle, CalendarDays, FileCheck2, MailCheck, SkipForward, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { moduleApi } from '../../lib/api';
import { StatusFilterChips } from '../ui/StatusFilterChips';
import { StatusTrackingTable, StatusTone } from '../ui/StatusTrackingTable';
import { Button } from '../ui/Button';
import { COLLECTE_STATUS_LABELS } from '../Evenementiel/collecteStatus';
import {
    PIPELINE_STAGE_LABELS, PIPELINE_STAGE_PENDING_LABELS, PIPELINE_STAGE_ORDER,
    PipelineStage, isTerminalStage, hasPassedPivot,
} from './pipelineStages';
import { getNextTransition, getSkipTransition, getAbandonTransition, PipelineTransition } from './pipelineTransitions';
import { PipelineActionModal } from './PipelineActionModal';
import { NouveauDossierModal } from './NouveauDossierModal';
import { CreerPrivatisationModal } from './CreerPrivatisationModal';

/**
 * Une ligne du suivi = UN DOSSIER, pas une privatisation. C'est la correction de fond d'Emeni
 * (2026-08-26) : le dossier naît d'un coup de téléphone, la privatisation n'existe qu'à la signature du
 * devis. `event_id` et `event_start_time` restent donc vides sur les cinq premières étapes, et c'est normal.
 */
interface DossierRow {
    dossier_id: string;
    label: string;
    type: string;
    email: string;
    organizer_email?: string;
    phone: string;
    stage: PipelineStage;
    stage_since?: string | null;
    formule_retenue: string;
    devis_amount_ttc?: number | null;
    devis_signed_at?: string | null;
    /** Le client a déposé son devis signé et personne ne l'a encore validé. */
    devis_signed_received_at?: string | null;
    closed_at?: string | null;
    lost_at?: string | null;
    lost_reason: string;
    /** null tant que le devis n'est pas signé — la privatisation n'entre dans Événementiel qu'à ce moment. */
    event_id?: string | null;
    event_start_time?: string | null;
    /** Reflété depuis Menus & Convives — jamais redupliqué ici (voir collecteStatus.ts). */
    collecte_status?: string | null;
    /** Le client a suivi le lien de la plaquette et rempli le questionnaire. */
    client_responded?: boolean;
    client_responded_at?: string | null;
    has_appointment?: boolean;
}

const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};
const fmtMontant = (n?: number | null) =>
    typeof n === 'number' && n > 0 ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n) : '—';

/** Jours écoulés depuis le dernier mouvement — c'est le vrai signal de relance d'un pipeline commercial. */
const joursSans = (since?: string | null): number | null => {
    if (!since) return null;
    const d = new Date(since).getTime();
    if (isNaN(d)) return null;
    return Math.floor((Date.now() - d) / 86400000);
};

const TONE_EN_COURS: StatusTone = { label: '', className: 'bg-brand-500/10 text-brand-600 dark:text-brand-400' };
const TONE_GAGNE: StatusTone = { label: '', className: 'bg-green-500/10 text-green-500' };
const TONE_PERDU: StatusTone = { label: '', className: 'bg-red-500/10 text-red-400' };

/**
 * Écran d'accueil du module « Suivi privatisation » — le pipeline commercial, du coup de téléphone jusqu'à
 * la clôture. Réutilise les composants de suivi déjà partagés avec Fournisseurs, Commandes et Extras
 * (StatusFilterChips, StatusTrackingTable) plutôt que d'en réinventer : c'est le même geste métier — filtrer
 * une liste par état et agir sur une ligne.
 *
 * Les pastilles de filtre affichent ce qui RESTE À FAIRE (« Devis à établir »), pas le nom du stade
 * (« Brief collecté ») : on vient ici chercher du travail en attente, pas un état de fait.
 */
export function SuiviPrivatisationModule() {
    const navigate = useNavigate();
    const [rows, setRows] = React.useState<DossierRow[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [search, setSearch] = React.useState('');
    const [filtre, setFiltre] = React.useState('all');
    const [erreur, setErreur] = React.useState('');

    const [action, setAction] = React.useState<{ row: DossierRow; transition: PipelineTransition } | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [erreurAction, setErreurAction] = React.useState('');

    const [ouvertureDossier, setOuvertureDossier] = React.useState(false);

    const charger = React.useCallback(() => {
        setLoading(true);
        moduleApi.getSuiviPrivatisation()
            .then((r: any) => setRows(Array.isArray(r) ? r : []))
            .catch((e: any) => setErreur(e?.message || 'Chargement impossible.'))
            .finally(() => setLoading(false));
    }, []);
    React.useEffect(() => { charger(); }, [charger]);

    const appliquer = async (payload: Record<string, any>) => {
        if (!action) return;
        setSaving(true);
        setErreurAction('');
        try {
            await moduleApi.transitionDossierPrivatisation(action.row.dossier_id, payload);
            setAction(null);
            charger();
        } catch (e: any) {
            setErreurAction(e?.message || "L'étape n'a pas pu être enregistrée.");
        } finally {
            setSaving(false);
        }
    };

    const creerPrivatisation = async (payload: Record<string, any>) => {
        if (!action) return;
        setSaving(true);
        setErreurAction('');
        try {
            await moduleApi.createEventFromDossier(action.row.dossier_id, payload);
            setAction(null);
            charger();
        } catch (e: any) {
            setErreurAction(e?.message || "La privatisation n'a pas pu être créée.");
        } finally {
            setSaving(false);
        }
    };

    const filtrees = rows.filter((r) => {
        const q = search.trim().toLowerCase();
        if (q && !r.label.toLowerCase().includes(q) && !(r.email || '').toLowerCase().includes(q) && !(r.phone || '').includes(q)) return false;
        if (filtre !== 'all' && r.stage !== filtre) return false;
        return true;
    });

    // Une pastille par stade réellement présent : afficher les quatorze en permanence, dont douze à zéro,
    // noierait les deux qui comptent.
    const options = [...PIPELINE_STAGE_ORDER, 'perdue', 'annulee']
        .map((s) => ({ value: s, label: PIPELINE_STAGE_PENDING_LABELS[s as PipelineStage], count: rows.filter((r) => r.stage === s).length }))
        .filter((o) => o.count > 0);

    const ton = (r: DossierRow): StatusTone => {
        const base = r.stage === 'cloturee' ? TONE_GAGNE : isTerminalStage(r.stage) ? TONE_PERDU : TONE_EN_COURS;
        return { ...base, label: PIPELINE_STAGE_LABELS[r.stage] || r.stage };
    };

    const lancer = (row: DossierRow, transition: PipelineTransition) => {
        setErreurAction('');
        setAction({ row, transition });
    };

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-xl font-bold text-content tracking-tight flex items-center gap-2">
                        <Briefcase size={18} /> Suivi privatisation
                    </h1>
                    <p className="text-content-muted text-sm mt-0.5">
                        Du coup de téléphone du client jusqu'à la clôture du service.
                    </p>
                </div>
                {/* « Nouvelle demande » et non « Suivre une privatisation » : il n'y a plus de privatisation
                    existante à rattacher, c'est le dossier qui la fera naître à la signature du devis. */}
                <Button variant="primary" size="sm" onClick={() => setOuvertureDossier(true)}>
                    <Plus size={13} /> Nouvelle demande
                </Button>
            </div>

            {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}

            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un client..."
                        className="bg-surface border border-border rounded-lg pl-8 pr-3 py-1.5 text-xs text-content outline-none focus:border-brand-500 w-56" />
                </div>
                <StatusFilterChips options={options} value={filtre} onChange={setFiltre} />
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>
            ) : (
                <StatusTrackingTable<DossierRow>
                    rows={filtrees}
                    getRowKey={(r) => r.dossier_id}
                    getStatus={ton}
                    onRowClick={(r) => navigate(`/suivi-privatisation/${r.dossier_id}`)}
                    emptyMessage={rows.length === 0
                        ? "Aucune demande en cours — ouvrez un dossier avec « Nouvelle demande » dès le premier appel."
                        : 'Aucun dossier ne correspond à ce filtre.'}
                    columns={[
                        {
                            key: 'client', header: 'Client', render: (r) => (
                                <div className="min-w-0">
                                    <p className="font-bold text-content truncate">{r.label}</p>
                                    <p className="text-[11px] text-content-muted truncate flex items-center gap-1">
                                        {r.phone ? <><Phone size={10} /> {r.phone}</> : r.email}
                                    </p>
                                    {r.formule_retenue && <p className="text-[11px] text-content-muted truncate">{r.formule_retenue}</p>}
                                    {r.lost_reason && <p className="text-[11px] text-red-400 truncate">{r.lost_reason}</p>}
                                </div>
                            )
                        },
                        {
                            key: 'date', header: 'Événement', render: (r) => (
                                // Avant la signature, il n'y a PAS de date : ne rien inventer ici est le sens
                                // même de la refonte. « Pas encore fixée » dit la vérité du dossier.
                                r.event_start_time ? (
                                    <span className="text-content-muted flex items-center gap-1 whitespace-nowrap">
                                        <CalendarDays size={11} /> {fmtDate(r.event_start_time)}
                                    </span>
                                ) : (
                                    <span className="text-content-muted italic text-[11px] whitespace-nowrap">pas encore fixée</span>
                                )
                            )
                        },
                        {
                            key: 'prochaine', header: 'Prochaine action', render: (r) => {
                                const t = getNextTransition(r.stage, { hasSignedDevis: !!r.devis_signed_received_at });
                                return (
                                    <div className="min-w-0">
                                        {t ? <span className="text-content-muted">{t.actionLabel}</span>
                                           : <span className="text-content-muted italic">Rien à faire</span>}
                                        {/* Un client qui a répondu est le dossier qu'il faut rappeler AUJOURD'HUI.
                                            Sans ce repère, l'information dormait dans RDV Pro et le dossier
                                            paraissait au point mort. Signalé par Emeni (2026-08-26). */}
                                        {r.client_responded && (
                                            <span className="mt-0.5 flex items-center gap-1 text-[10px] font-bold text-green-600 dark:text-green-400 whitespace-nowrap">
                                                <MailCheck size={11} /> Le client a répondu
                                                {r.has_appointment ? ' · RDV pris' : ''}
                                            </span>
                                        )}
                                        {/* Même geste que ci-dessus : un devis signé déposé et pas encore
                                            validé est le dossier à ouvrir aujourd'hui. */}
                                        {r.devis_signed_received_at && !r.devis_signed_at && (
                                            <span className="mt-0.5 flex items-center gap-1 text-[10px] font-bold text-green-600 dark:text-green-400 whitespace-nowrap">
                                                <FileCheck2 size={11} /> Devis signé reçu — à vérifier
                                            </span>
                                        )}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'immobile', header: 'Sans mouvement', render: (r) => {
                                if (isTerminalStage(r.stage) || r.stage === 'cloturee') return <span className="text-content-muted">—</span>;
                                const j = joursSans(r.stage_since);
                                if (j === null) return <span className="text-content-muted">—</span>;
                                // Au-delà de deux semaines sans nouvelle, un dossier commercial se relance.
                                const alerte = j >= 14;
                                return (
                                    <span className={`whitespace-nowrap ${alerte ? 'text-amber-600 dark:text-amber-400 font-bold' : 'text-content-muted'}`}>
                                        {j === 0 ? "aujourd'hui" : `${j} jour${j > 1 ? 's' : ''}`}
                                    </span>
                                );
                            }
                        },
                        {
                            key: 'devis', header: 'Devis', render: (r) => (
                                <span className="flex items-center gap-1.5 whitespace-nowrap">
                                    <span className="text-content">{fmtMontant(r.devis_amount_ttc)}</span>
                                    {r.devis_signed_at && <FileCheck2 size={13} className="text-green-500" aria-label="Devis signé reçu" />}
                                </span>
                            )
                        },
                        {
                            key: 'menus', header: 'Menus', render: (r) => {
                                // Le statut appartient à Menus & Convives : on le REFLÈTE, on ne le recalcule pas.
                                if (!r.collecte_status) {
                                    return <span className="text-content-muted text-[11px] italic">
                                        {hasPassedPivot(r.stage) ? 'à lancer' : '—'}
                                    </span>;
                                }
                                return <span className="text-[11px] text-content-muted">{COLLECTE_STATUS_LABELS[r.collecte_status] || r.collecte_status}</span>;
                            }
                        },
                    ]}
                    renderActions={(r) => {
                        const suivante = getNextTransition(r.stage, { hasSignedDevis: !!r.devis_signed_received_at });
                        const passer = getSkipTransition(r.stage);
                        const abandon = getAbandonTransition(r.stage);
                        return (
                            <>
                                {suivante && (
                                    <Button size="sm" variant="primary" onClick={() => lancer(r, suivante)}>
                                        {suivante.actionLabel} <ArrowRight size={12} />
                                    </Button>
                                )}
                                {passer && (
                                    <Button size="sm" variant="ghost" onClick={() => lancer(r, passer)}>
                                        <SkipForward size={12} /> Passer
                                    </Button>
                                )}
                                {abandon && (
                                    <Button size="sm" variant="ghost" onClick={() => lancer(r, abandon)}>
                                        <XCircle size={12} /> {abandon.actionLabel}
                                    </Button>
                                )}
                            </>
                        );
                    }}
                />
            )}

            {/* La création de la privatisation a son propre écran : elle exige une date, des horaires et un
                calendrier, que la modale d'étape ordinaire ne sait pas demander. Le serveur refuse
                d'ailleurs volontairement d'atteindre ce stade par une simple transition. */}
            {action && action.transition.opensForm === 'creer_privatisation' ? (
                <CreerPrivatisationModal
                    label={action.row.label}
                    saving={saving}
                    error={erreurAction}
                    onCancel={() => setAction(null)}
                    onConfirm={creerPrivatisation}
                />
            ) : action ? (
                <PipelineActionModal
                    label={action.row.label}
                    transition={action.transition}
                    destinataire={action.row.organizer_email || action.row.email}
                    destinataireEstReferent={!!action.row.organizer_email}
                    saving={saving}
                    error={erreurAction}
                    onCancel={() => setAction(null)}
                    onConfirm={appliquer}
                />
            ) : null}

            {ouvertureDossier && (
                <NouveauDossierModal
                    onClose={() => setOuvertureDossier(false)}
                    onCree={(id) => { setOuvertureDossier(false); navigate(`/suivi-privatisation/${id}`); }}
                />
            )}
        </div>
    );
}
